<template>
  <div v-if="visible" class="overlay">
    <div class="popup" data-cy="sync-popup">
      <div class="content-wrapper">
        <div class="text-container">
          <div class="title">
            {{ $t("sync.status.in-progress") }}
          </div>
          <div class="subtitle">
            <span class="text-animation">{{ syncMessage }}</span>
          </div>
          <div class="progress-container">
            <div class="progress-bar" :style="{ width: `${progress}%` }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  initiateOrderSync,
  initiatePaymentSync,
} from "@/services/sync-service";
import { unsyncedOrdersCount } from "@/db/orders";
import { unsyncedPaymentsCount } from "@/db/payments";
import { ref, onMounted, defineComponent } from "vue";
import { LogLevel, LogService } from "@/services/logging";
import Timeout from "await-timeout";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "SyncInProgress",
  emits: ["sync-success"],

  setup(_, { emit }) {
    const { t } = useI18n({});

    const visible = ref(true);
    const progress = ref(0);
    const syncMessage = ref(t("sync.instructions"));
    const totalBookings = ref(0);
    const remainingBookings = ref(0);

    const retryInterval = 10_000; // 10 seconds between checks
    const maxDuration = 10 * 60 * 1000; // 5 minutes

    let syncTimer: number | null = null;

    /**
     * Total rows still awaiting sync across BOTH queues the till close depends on:
     * completed/preserved orders and their (offline/failed) payments. The close is
     * gated until both drain to zero.
     */
    async function remainingToSync(): Promise<number> {
      const [orders, payments] = await Promise.all([
        unsyncedOrdersCount(),
        unsyncedPaymentsCount(),
      ]);
      return orders + payments;
    }

    function finishSync(success: boolean) {
      if (syncTimer) {
        clearInterval(syncTimer);
        syncTimer = null;
      }

      if (success) {
        syncMessage.value = t("sync.status.success");
        progress.value = 100;

        // Only hide the popup when successful
        setTimeout(() => {
          visible.value = false;
          emit("sync-success", true);
        }, 500);
      } else {
        syncMessage.value = t("sync.status.failure");

        // Emit the failure event
        setTimeout(() => {
          visible.value = false;
          emit("sync-success", false);
        }, 1000);

        LogService.info(
          `Sync failed after trying for ${maxDuration / 1000} seconds. ${
            remainingBookings.value
          } bookings still need to be synchronized.`
        );
      }
    }

    async function startSyncLoop() {
      const startTime = Date.now();

      // Reset sync state when starting/retrying
      progress.value = 0;
      syncMessage.value = t("sync.instructions");

      syncTimer = window.setInterval(async () => {
        const elapsed = Date.now() - startTime;
        if (elapsed >= maxDuration) {
          remainingBookings.value = await remainingToSync();
          finishSync(remainingBookings.value === 0);
          return;
        }

        try {
          await initiateOrderSync();
          await initiatePaymentSync();
          await Timeout.set(2000);
          remainingBookings.value = await remainingToSync();

          if (remainingBookings.value === 0) {
            finishSync(true);
            return;
          }

          // Update progress
          const synced = totalBookings.value - remainingBookings.value;
          progress.value = Math.min(
            Math.floor((synced / totalBookings.value) * 100),
            99
          );
        } catch (err) {
          LogService.info(
            "Sync: error when syncing orders",
            { params: err },
            LogLevel.Error
          );
        }
      }, retryInterval);
    }

    async function initializeSync() {
      totalBookings.value = await remainingToSync();
      remainingBookings.value = totalBookings.value;

      if (totalBookings.value === 0) {
        finishSync(true);
      } else {
        startSyncLoop();
      }
    }

    onMounted(() => {
      initializeSync();
    });

    return {
      visible,
      progress,
      syncMessage,
      totalBookings,
      remainingBookings,
    };
  },
});
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.popup {
  background: white;
  padding: 1.75rem 2rem;
  border-radius: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: fade-in 0.4s ease-out;
  min-width: 320px;
  max-width: 90%;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.text-container {
  width: 100%;
}

.title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #333;
}

.subtitle {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
  min-height: 1.5rem;
}

.text-animation {
  display: inline-block;
  animation: fade-text 0.5s ease-out;
}

.progress-container {
  width: 100%;
  height: 6px;
  background-color: #eaeaea;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.progress-bar {
  height: 100%;
  background-color: #3498db;
  border-radius: 3px;
  transition: width 0.5s ease;
  position: relative;
  overflow: hidden;
}

.progress-bar::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
}

.retry-button {
  margin-top: 1.25rem;
  padding: 0.5rem 1.5rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.retry-button:hover {
  background-color: #2980b9;
}

.retry-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.4);
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fade-text {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
